import { useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { ArrowRight, Coins, Gift, Lock, Plus, ShoppingCart, TrendingUp, Zap } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { startOfMonth, subWeeks, startOfWeek, endOfWeek, format, min } from "date-fns";
import { StudentLayout } from "@/components/student/layout/StudentLayout";
import { useStudentProfile } from "@/contexts/StudentProfileContext";
import { useAuth } from "@/hooks/use-auth-compat";
import { fetchPublicSubscriptionPlans } from "@/services/teacherSubscriptionService";
import {
  fetchStudentCoinSpends,
  fetchStudentCoinSummary,
  fetchStudentRegistrationGrant,
} from "@/services/studentProfileService";
import { fetchCoinPackages, fetchPurchaseOrders, type PurchaseOrderHistory } from "@/services/coinPurchaseService";
import { CoinSpendTable } from "@/components/coins/CoinSpendTable";
import { PurchaseReceiptModal } from "@/components/coins/PurchaseReceiptModal";
import { Skeleton } from "@/components/ui/skeleton";
import { currencySymbol } from "@/lib/currency";
import { packAmountMinorFor } from "@/lib/paymentAmounts";
import { PricesRetryAlert } from "@/components/coins/PricesRetryAlert";
import { cn } from "@/lib/utils";

function formatCoinDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
}

/** Builds weekly spend buckets for the last 4 weeks (oldest → newest). */
function buildWeeklyBuckets(rows: { createdAt: string; amount: number; reason?: string }[]) {
  return Array.from({ length: 4 }, (_, i) => {
    const weekStart = startOfWeek(subWeeks(new Date(), 3 - i), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    const spent = rows
      .filter((r) => r.amount < 0 && new Date(r.createdAt) >= weekStart && new Date(r.createdAt) <= weekEnd)
      .reduce((sum, r) => sum + Math.abs(r.amount), 0);
    return { label: format(weekStart, "MMM d"), spent };
  });
}

export default function StudentCoinsPage() {
  usePageTitle('BMC Coins | BookMyCoaching');
  const auth = useAuth();
  const navigate = useNavigate();
  const token = auth.user?.access_token ?? "";
  const { profile } = useStudentProfile();
  const registrationDate = profile?.createdDate ? new Date(profile.createdDate) : undefined;

  // Same server-resolved currency as /pricing and the top-up pages (ADR-151). This page shows what
  // coins are WORTH, so quoting it in a different currency than the page that sells them read as a
  // different price for the same thing -- it showed "$0.35 at base rate" while every purchase
  // surface quoted rupees.
  //
  // /marketplace/geo-defaults, used here before, falls back to DEFAULT_CURRENCY = "USD"; the
  // resolver behind /pricing falls back to TEACHER_DEFAULT_CURRENCY = "INR", and is what the
  // forced-currency switch acts on.
  const {
    data: plansForCurrency,
    isError: plansError,
    refetch: refetchPlans,
  } = useQuery({
    queryKey: ["public", "teacher-plans"],
    queryFn: fetchPublicSubscriptionPlans,
    staleTime: 5 * 60 * 1000,
  });
  // SCRUM-365: the plans currency exactly as returned -- no INR fallback (D2).
  const rawCurrency = plansForCurrency?.currency ?? null;
  const sym = currencySymbol(rawCurrency);
  const showPlansError = plansError;

  const [receiptOrder, setReceiptOrder] = useState<PurchaseOrderHistory | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  const { data: registrationGrant } = useQuery({
    queryKey: ["studentRegistrationGrant"],
    queryFn: () => fetchStudentRegistrationGrant(token),
    enabled: !!token,
  });

  const { data: coinSummary, isLoading: coinSummaryLoading } = useQuery({
    queryKey: ["studentCoinSummary"],
    queryFn: () => fetchStudentCoinSummary(token),
    enabled: !!token,
  });

  // Fetch analytics data: from the earlier of (start of current month) or (4 weeks ago),
  // whichever comes first. This ensures the full current month is always covered even late
  // in the month when 4 weeks ago would overshoot the month boundary, while still providing
  // enough history for the 4-week trend bars.
  // The query key includes the current month so React Query never serves cross-month stale data.
  const analyticsFrom = min([startOfMonth(new Date()), subWeeks(new Date(), 4)]);
  const analyticsMonthKey = format(new Date(), "yyyy-MM");
  const { data: recentSpends } = useQuery({
    queryKey: ["studentRecentSpends", analyticsMonthKey],
    queryFn: () =>
      fetchStudentCoinSpends(token, {
        page: 0,
        size: 200,
        dateFrom: analyticsFrom,
      }),
    enabled: !!token,
    staleTime: 2 * 60 * 1000,
  });

  const { data: packages } = useQuery({
    queryKey: ["coinPackages"],
    queryFn: fetchCoinPackages,
    staleTime: 5 * 60 * 1000,
  });

  const { data: purchaseOrders } = useQuery({
    queryKey: ["studentPurchaseOrders"],
    queryFn: () => fetchPurchaseOrders(token),
    enabled: !!token,
    staleTime: 2 * 60 * 1000,
  });

  const {
    data: coinSpendsPage,
    isLoading: coinSpendsLoading,
    isError: coinSpendsError,
  } = useQuery({
    queryKey: ["studentCoinSpends", { page: pageIndex, name: searchTerm, dateFrom, dateTo }],
    queryFn: () =>
      fetchStudentCoinSpends(token, { page: pageIndex, size: 10, name: searchTerm || undefined, dateFrom, dateTo }),
    enabled: !!token,
  });

  // --- Derived analytics ---
  const available = coinSummary?.available ?? null;
  const held = coinSummary?.held ?? 0;
  const heldThreadCount = coinSummary?.heldThreadCount ?? 0;
  const inquiriesRemaining = available != null ? Math.floor(available / 30) : null;

  const recentRows = recentSpends?.content ?? [];
  const thisMonthStart = startOfMonth(new Date());
  const monthlyCharges = recentRows
    .filter((r) => r.amount < 0 && new Date(r.createdAt) >= thisMonthStart)
    .reduce((sum, r) => sum + Math.abs(r.amount), 0);
  const monthlyPurchases = recentRows
    .filter((r) => r.reason === "PURCHASE" && new Date(r.createdAt) >= thisMonthStart)
    .reduce((sum, r) => sum + r.amount, 0);
  const monthlyInquiries = Math.floor(monthlyCharges / 30);

  const weeklyBuckets = buildWeeklyBuckets(recentRows);
  const maxWeeklySpend = Math.max(...weeklyBuckets.map((b) => b.spent), 1);

  // Top-up suggestion: package that covers ~2 months at current burn rate
  const suggestedPkg = packages?.find((p) => p.coins >= monthlyCharges * 2) ?? packages?.[packages.length - 1];
  const suggestedMonthsCovered = suggestedPkg && monthlyCharges > 0
    ? Math.round(suggestedPkg.coins / monthlyCharges)
    : null;

  // Per-inquiry real money cost (base-rate package)
  const basePkg = packages?.[0];
  // No figure (null) until the backend currency is known and supported (D2).
  const baseMinorOn = basePkg ? packAmountMinorFor(basePkg, rawCurrency) : null;
  const basePricePerCoin = basePkg && baseMinorOn !== null ? baseMinorOn / 100 / basePkg.coins : null;
  const perInquiryCost = basePricePerCoin != null ? basePricePerCoin * 30 : null;

  return (
    <StudentLayout>
      <main className="flex-1 overflow-auto">
        <div className="px-4 sm:px-6 py-4">

          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-black text-slate-900">BMC Coins</h1>
              <p className="text-sm text-slate-500 mt-0.5">Your balance &amp; transaction history</p>
            </div>
            <Link
              to="/buy-coins"
              className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Top Up
            </Link>
          </div>

          {/* Primary stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            {/* Available */}
            <div className="col-span-2 sm:col-span-1 bg-gradient-to-br from-indigo-600 to-violet-700 rounded-xl p-4 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Coins className="w-4 h-4 text-white/70" />
                <span className="text-xs font-semibold text-white/70">Available</span>
              </div>
              {coinSummaryLoading ? (
                <>
                  <Skeleton className="h-8 w-20 bg-white/20" />
                  <Skeleton className="h-3 w-32 bg-white/20 mt-2" />
                </>
              ) : available != null ? (
                <>
                  <p className="text-3xl font-black leading-none">{available.toLocaleString()}</p>
                  <p className="text-xs text-white/60 mt-1">
                    {inquiriesRemaining != null
                      ? `= ${inquiriesRemaining} ${inquiriesRemaining === 1 ? "inquiry" : "inquiries"} remaining`
                      : "BMC Coins ready to use"}
                  </p>
                  {showPlansError ? (
                    <PricesRetryAlert onRetry={() => void refetchPlans()} className="mt-2 p-3" />
                  ) : perInquiryCost != null && (
                    <p className="text-xs text-white/40 mt-0.5">
                      each inquiry ≈ {sym}{perInquiryCost < 1 ? perInquiryCost.toFixed(2) : Math.round(perInquiryCost)}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-lg font-bold leading-none">—</p>
                  <p className="text-xs text-white/60 mt-1">Granted on first registration</p>
                </>
              )}
            </div>

            {/* Held */}
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-bold text-slate-500">Held</span>
              </div>
              {coinSummaryLoading ? (
                <>
                  <Skeleton className="h-8 w-16 bg-slate-200" />
                  <Skeleton className="h-3 w-28 bg-slate-200 mt-2" />
                </>
              ) : (
                <>
                  <p className="text-3xl font-black leading-none text-slate-800">{held.toLocaleString()}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {heldThreadCount > 0
                      ? `across ${heldThreadCount} open ${heldThreadCount === 1 ? "inquiry" : "inquiries"}`
                      : "no open inquiries"}
                  </p>
                  {heldThreadCount > 0 && (
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                      Released or charged when each inquiry closes.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Signup Bonus */}
            <div className="bg-amber-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-bold text-amber-700">Signup Bonus</span>
              </div>
              {registrationGrant ? (
                <>
                  <p className="text-3xl font-black leading-none text-slate-800">+{registrationGrant.amount.toLocaleString()}</p>
                  <p className="text-xs text-amber-600 mt-1">on {formatCoinDate(registrationGrant.createdAt)}</p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-black leading-none text-slate-300">—</p>
                  <p className="text-xs text-slate-400 mt-1">one-time on registration</p>
                </>
              )}
            </div>
          </div>

          {/* Activity tiles — only shown once recent data loads */}
          {/* New-user empty state — shown when no spending has happened yet */}
          {!coinSpendsLoading && recentRows.length === 0 && available != null && available > 0 && (
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-4 mb-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-indigo-800">You have {available.toLocaleString()} coins ready to use</p>
                <p className="text-xs text-indigo-500 mt-0.5">
                  Browse the marketplace and send your first inquiry — each one costs just 30 coins.
                </p>
              </div>
              <Link
                to="/catalog"
                className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-colors shrink-0"
              >
                Browse Teachers <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}

          {recentRows.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
              {/* This month */}
              <div className="bg-indigo-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingCart className="w-4 h-4 text-indigo-500" />
                  <span className="text-xs font-bold text-indigo-600">This Month</span>
                </div>
                <p className="text-2xl font-black leading-none text-slate-800">{monthlyCharges.toLocaleString()}</p>
                <p className="text-xs text-indigo-400 mt-1">
                  coins spent · {monthlyInquiries} {monthlyInquiries === 1 ? "inquiry" : "inquiries"}
                </p>
                {monthlyPurchases > 0 && (
                  <p className="text-xs text-emerald-600 mt-1">+{monthlyPurchases.toLocaleString()} purchased</p>
                )}
              </div>

              {/* Weekly trend */}
              <div className="bg-violet-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-violet-500" />
                  <span className="text-xs font-bold text-violet-600">4-Week Trend</span>
                </div>
                <div className="flex items-end gap-1.5 h-10">
                  {weeklyBuckets.map((bucket, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className={cn(
                          "w-full rounded-t-sm transition-all",
                          bucket.spent > 0 ? "bg-violet-400" : "bg-violet-100",
                        )}
                        style={{ height: `${Math.max((bucket.spent / maxWeeklySpend) * 36, bucket.spent > 0 ? 6 : 4)}px` }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex gap-1.5 mt-1">
                  {weeklyBuckets.map((bucket, i) => (
                    <p key={i} className="flex-1 text-center text-xs text-slate-400 truncate">{bucket.label}</p>
                  ))}
                </div>
              </div>

              {/* Top-up suggestion */}
              <div className="col-span-2 sm:col-span-1 bg-emerald-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs font-bold text-emerald-700">Suggested Pack</span>
                  </div>
                  {suggestedPkg && monthlyCharges > 0 && (
                    <Link
                      to="/buy-coins"
                      className="sm:hidden inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-800 mb-2"
                    >
                      View <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
                {suggestedPkg && monthlyCharges > 0 ? (
                  <>
                    <p className="text-2xl font-black leading-none text-slate-800">{suggestedPkg.coins.toLocaleString()}</p>
                    <p className="text-xs text-emerald-600 mt-1">
                      coins{suggestedMonthsCovered && suggestedMonthsCovered > 0
                        ? ` · ~${suggestedMonthsCovered} months at your rate`
                        : ""}
                    </p>
                    <Link
                      to="/buy-coins"
                      className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-800 mt-2"
                    >
                      View pack <ArrowRight className="w-3 h-3" />
                    </Link>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-slate-400 mt-1">No activity yet</p>
                    <p className="text-xs text-slate-400 mt-0.5">Suggestion based on your spending</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Low balance nudge */}
          {!coinSummaryLoading && available != null && available < 30 && (
            <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-3">
              <p className="text-xs font-medium text-amber-700">
                You have fewer than 30 coins — not enough for an inquiry.
              </p>
              <Link to="/buy-coins" className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-900 shrink-0 ml-3">
                Top up <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}

          {/* Transaction history */}
          {/* Purchase history — copyable payment IDs for disputes / refunds */}
          {purchaseOrders && purchaseOrders.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Purchase History</p>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                {purchaseOrders.map((order, i) => {
                  const sym = currencySymbol(order.currency as "INR" | "USD");
                  const amount = order.amount / 100;
                  const fmtAmount = `${sym}${Number.isInteger(amount) ? amount.toLocaleString() : amount.toFixed(2)}`;
                  const date = new Date(order.paidAt ?? order.createdAt).toLocaleDateString([], {
                    year: "numeric", month: "short", day: "numeric",
                  });
                  const statusColor = order.status === "PAID"
                    ? "text-emerald-600 bg-emerald-50"
                    : order.status === "DISPUTED"
                    ? "text-amber-600 bg-amber-50"
                    : "text-slate-500 bg-slate-100";
                  return (
                    <button
                      key={order.orderId}
                      onClick={() => setReceiptOrder(order)}
                      className={cn(
                        "w-full text-left flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors",
                        i > 0 && "border-t border-slate-100",
                      )}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-800 truncate">
                          {order.coins.toLocaleString()} coins · {fmtAmount}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">{date}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <span className={cn("text-xs font-bold px-2 py-0.5 rounded-full", statusColor)}>
                          {order.status === "PAID" ? "Paid" : order.status === "DISPUTED" ? "Disputed" : "Refunded"}
                        </span>
                        <span className="hidden sm:inline text-xs text-indigo-500 font-semibold">View receipt</span>
                        <ArrowRight className="w-3.5 h-3.5 text-indigo-400" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <PurchaseReceiptModal order={receiptOrder} onClose={() => setReceiptOrder(null)} />

          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Transaction History</p>
          <CoinSpendTable
            counterpartLabel="Description"
            page={coinSpendsPage}
            isLoading={coinSpendsLoading}
            isError={coinSpendsError}
            searchTerm={searchTerm}
            onSearchChange={(v) => { setSearchTerm(v); setPageIndex(0); }}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={(d) => { setDateFrom(d); setPageIndex(0); }}
            onDateToChange={(d) => { setDateTo(d); setPageIndex(0); }}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            onRowClick={(row) => { if (row.threadId) navigate(`/inquiry/${row.threadId}`); }}
            registrationDate={registrationDate}
          />
        </div>
      </main>
    </StudentLayout>
  );
}
